import React, { useEffect, useMemo, useState } from "react"

import { Flex, Spin } from "antd"
import cls from "classname"
import { useDispatch, useSelector } from "react-redux"

import SelectComponent from "../../../../../Components/Select"
import Images from "../../../../../Images"
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle"
import MonitoringBlockHeader from "../../../components/Monitoring/MonitoringBlockHeader/MonitoringBlockHeader"
import MonthCell from "../../../components/Monitoring/MonthCell/MonthCell"
import { getMonthCellLines } from "../../../components/Monitoring/monthCellLines"
import MonthStrip from "../../../components/Monitoring/MonthStrip/MonthStrip"
import { PROGRAMS } from "../../../components/Monitoring/programLimits"
import PermissionPlaceholder from "../../../components/PermissionPlaceholder"
import useHealthAccessRestricted from "../../../hooks/useHealthAccessRestricted"
import HealthActions from "../../../reducer"
import InfoLine from "../../../VAMP/components/InfoLine/InfoLine"
import MonitoringProgramEdges from "../../../VAMP/components/MonitoringProgram/MonitoringProgramEdges/MonitoringProgramEdges"
import ProgressBar from "../../../VAMP/components/MonitoringProgram/ProgressBar/ProgressBar"
import SectionHeader from "../../../VAMP/components/SectionHeader/SectionHeader"
import { useIsStripeConnected } from "../../hooks/useIsStripeConnected"
import useMonitoringProgramProgress from "../../hooks/useMonitoringProgramProgress"
import { buildLockedTimeline } from "../../utils/buildLockedTimeline"
import RegionNotEligiblePlaceholder from "../RegionNotEligiblePlaceholder/RegionNotEligiblePlaceholder"

import "../../../VAMP/components/MonitoringProgram/MonitoringProgram.less"

const statusMessageVariantMap = {
  pending: "neutral",
  compliant: "success",
  warning: "warning",
  under_monitoring: "warning",
  no_status: "neutral"
}

const EFMMonitoringProgram = ({ business_account_id }) => {
  const dispatch = useDispatch()
  const { isMobile } = useSelector((state) => state.nav)
  const { efmMonitoringProgramChart, efmMonitoringProgramChartFetching } = useSelector(
    (state) => state.health
  )
  const isStripeConnected = useIsStripeConnected()
  const rawData = efmMonitoringProgramChart ?? {}
  // Stripe disconnected: hide any (possibly stale) real snapshot history behind a
  // fully-locked timeline instead, matching a brand-new account's empty state.
  const data = isStripeConnected
    ? rawData
    : { ...rawData, current_status: null, timeline: buildLockedTimeline() }
  const currentStatus = data?.current_status
  const { isShowDemoData } = useDemoDataToggle()
  const isRestricted = useHealthAccessRestricted()

  const yearOptions = useMemo(() => {
    const years = efmMonitoringProgramChart?.available_years ?? [new Date().getFullYear()]
    return years.map((year) => ({ label: year, value: year }))
  }, [efmMonitoringProgramChart])
  const [year, setYear] = useState(yearOptions?.[yearOptions.length - 1]?.value ?? null)

  useEffect(() => {
    if (!efmMonitoringProgramChart?.available_years?.includes(year)) {
      setYear(yearOptions?.[yearOptions.length - 1]?.value ?? null)
    }
  }, [yearOptions])

  useEffect(() => {
    if (isStripeConnected) {
      dispatch(
        HealthActions.getEfmMonitoringProgramChartRequest({
          business_account: business_account_id,
          year
        })
      )
    }
  }, [business_account_id, year, isShowDemoData, isStripeConnected])
  const isRegionExcluded = data.region === "excluded"

  const isLiveYear = data?.current_month?.year === year
  // The program window bar renders only while the account is in the program — the hook gates on
  // an under_monitoring status.
  const {
    isShowProgressBar,
    timeLineSegments,
    startDate,
    endDate,
    edgesIntervalWidth,
    edgesStartOffset,
    isFinishTextHidden
  } = useMonitoringProgramProgress({ data })

  return (
    <Flex vertical>
      <Spin
        wrapperClassName="monitoring-program-chart-spin"
        spinning={efmMonitoringProgramChartFetching}
      >
        <Flex
          gap={10}
          align="flex-start"
          justify="space-between"
          className="monitoring-program-header"
        >
          <div className="monitoring-program-header__main">
            <SectionHeader
              title="Excessive Fraud Merchant Program (EFM)"
              image={Images.mastercard_3d}
              marginBottom={8}
            />
            {!isRestricted && !isRegionExcluded && currentStatus && (
              <MonitoringBlockHeader
                blockId="efm"
                program={PROGRAMS.EFM}
                status={currentStatus}
                currentMonth={data?.current_month}
                region={data?.region}
              />
            )}
          </div>
          <SelectComponent
            value={year}
            onChange={setYear}
            options={yearOptions}
            style={{ width: isMobile ? "100%" : 100 }}
            className="monitoring-program-year-select"
            disabled={isRestricted}
          />
        </Flex>

        <div
          className={cls("mc-region-gated-content", {
            "is-region-excluded": isRegionExcluded
          })}
        >
          {!isMobile && isShowProgressBar && !isRestricted && (
            <Flex vertical gap={8} className="monitoring-program-window">
              <MonitoringProgramEdges
                intervalWidth={edgesIntervalWidth}
                startOffset={edgesStartOffset}
                isFinishTextHidden={isFinishTextHidden}
                startDate={startDate}
                endDate={endDate}
              />
              <Flex gap={4} style={{ width: "100%" }}>
                {timeLineSegments.map((segment, index) => (
                  <ProgressBar
                    key={index}
                    progress={segment.progress}
                    style={{ width: segment.width }}
                  />
                ))}
              </Flex>
            </Flex>
          )}

          {isRestricted ? (
            <PermissionPlaceholder minHeight={150} />
          ) : (
            <MonthStrip
              program={PROGRAMS.EFM}
              timeline={data?.timeline}
              year={year}
              currentMonth={data?.current_month}
              region={data?.region}
              isMobile={isMobile}
              renderCell={(month, { popover }) => (
                <MonthCell
                  key={month.month}
                  month={month}
                  lines={getMonthCellLines({ program: PROGRAMS.EFM, month })}
                  isCurrent={isLiveYear && month.month === data?.current_month?.month}
                  popover={popover}
                  isMobile={isMobile}
                />
              )}
            />
          )}

          {!isRestricted && !isRegionExcluded && currentStatus?.tooltip && (
            <InfoLine
              text={currentStatus.tooltip}
              variant={statusMessageVariantMap[currentStatus?.status] || "neutral"}
              style={{ marginTop: "16px" }}
            />
          )}

          {isRegionExcluded && <RegionNotEligiblePlaceholder programName="EFM" />}
        </div>
      </Spin>
    </Flex>
  )
}

export default EFMMonitoringProgram
